import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAktivIroda } from '../iroda/AktivIroda.tsx';
import { supabase } from '../lib/supabase.ts';
import { formatDatum } from '../lib/formaz.ts';
import { halozatNev } from '../super/regisztracioLista.ts';
import { facebookLink, irodaSorUrlapbol, irodaUrlapHibak, irodaUrlapSorbol, irodaUrlapUres, type IrodaUrlapAdat } from '../super/irodaUrlap.ts';
import KiosztasBlokk from '../super/KiosztasBlokk.tsx';
import KorzetBlokk from '../super/KorzetBlokk.tsx';
import MunkaidoSzerkeszto from '../super/MunkaidoSzerkeszto.tsx';
import { Betolto, Fokonyv, Gomb, HibaUzenet, Mezo, OldalFej, Parbeszed, Valaszto } from '../ui/elemek.tsx';

// A publikus oldal címe környezetenként (helyben az Astro dev-szerver); alapértelmezés az éles domain.
const ELO_ALAP = (import.meta.env.VITE_PUBLIKUS_ALAP as string | undefined) ?? 'https://ingatlanertekmeghatarozas.hu';

export default function SuperIrodaSzerkeszto() {
  const { id } = useParams<{ id: string }>();
  const uj = id === undefined;
  const navigate = useNavigate();
  const kliens = useQueryClient();
  const { beallit } = useAktivIroda();
  const [adat, setAdat] = useState<IrodaUrlapAdat>(irodaUrlapUres);
  const [probalt, setProbalt] = useState(false);
  const [megerosit, setMegerosit] = useState(false);
  const [mentve, setMentve] = useState(false);
  const [slugFoglalt, setSlugFoglalt] = useState(false);

  const betoltes = useQuery({
    queryKey: ['super-iroda', id],
    enabled: !uj,
    queryFn: async () => {
      const { data, error } = await supabase.from('offices').select('*').eq('id', id!).maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  useEffect(() => { if (betoltes.data) setAdat(irodaUrlapSorbol(betoltes.data)); }, [betoltes.data]);

  // Ha az iroda online regisztrációból jött létre, a cégadatok csak olvasásra látszanak (a forrás a regisztráció).
  const regisztracio = useQuery({
    queryKey: ['super-iroda-regisztracio', id],
    enabled: !uj,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('iroda_regisztraciok')
        .select('id, cegnev, szekhely, adoszam, ceg_forma, cegjegyzekszam, ev_nyilvantartasi_szam, szamlazasi_email, halozat_kulcs, halozat_egyeb, aszf_verzio, elfogadva_at')
        .eq('office_id', id!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const fbLink = facebookLink(ELO_ALAP, adat.slug);
  const [masolas, setMasolas] = useState<'kesz' | 'hiba' | null>(null);
  useEffect(() => setMasolas(null), [fbLink]);
  // A vágólap-hozzáférés megtagadható (nem biztonságos környezet, böngészőbeállítás): ilyenkor a link kijelölhető marad, és üzenet kéri a kézi másolást.
  const masol = async () => {
    try {
      await navigator.clipboard.writeText(fbLink!);
      setMasolas('kesz');
    } catch {
      setMasolas('hiba');
    }
  };

  const hibak = irodaUrlapHibak(adat);
  const ervenyes = Object.keys(hibak).length === 0;
  const mutat = <K extends keyof IrodaUrlapAdat>(k: K) => (ertek: IrodaUrlapAdat[K]) => { setMentve(false); setSlugFoglalt(false); setAdat((a) => ({ ...a, [k]: ertek })); };
  const szoveges = (k: 'slug' | 'nev' | 'halozat' | 'regio' | 'varos' | 'logoUrl' | 'kapcsolatEmail' | 'kapcsolatTelefon' | 'hivasTelefon') =>
    ({ value: adat[k], onChange: (e: { target: { value: string } }) => mutat(k)(e.target.value), hiba: probalt ? hibak[k] : undefined });

  const mentes = useMutation({
    mutationFn: async () => {
      const sor = irodaSorUrlapbol(adat, betoltes.data?.minoseg_elfogadva_at ?? null);
      if (uj) {
        const { data, error } = await supabase.from('offices').insert(sor).select('id').single();
        if (error) throw error;
        return data.id;
      }
      const { error } = await supabase.from('offices').update(sor).eq('id', id!);
      if (error) throw error;
      return id!;
    },
    onSuccess: async (ujId) => {
      await Promise.all([
        kliens.invalidateQueries({ queryKey: ['irodak'] }),
        kliens.invalidateQueries({ queryKey: ['super-irodak'] }),
        kliens.invalidateQueries({ queryKey: ['super-iroda', ujId] }),
      ]);
      setMentve(true);
      if (uj) void navigate(`/super/irodak/${ujId}`, { replace: true });
    },
    onError: (hiba) => { if ((hiba as { code?: string }).code === '23505') setSlugFoglalt(true); },
  });

  const kuldes = () => {
    setProbalt(true);
    if (!ervenyes) return;
    if (!uj && betoltes.data?.aktiv && !adat.aktiv) { setMegerosit(true); return; }
    mentes.mutate();
  };

  if (!uj && betoltes.isPending) return <Betolto />;
  if (!uj && betoltes.error) return <HibaUzenet hiba={betoltes.error} />;
  if (!uj && !betoltes.data) return <p>Nincs ilyen iroda.</p>;

  return (
    <form className="grid max-w-3xl gap-5" onSubmit={(e) => { e.preventDefault(); kuldes(); }} noValidate>
      <OldalFej cim={uj ? 'Új iroda' : adat.nev || 'Iroda szerkesztése'} leiras="Az iroda adatai, a „Hívjon most” gomb, a határidő és a hirdetési link." />
      <Fokonyv>

      <section className="grid gap-4 px-5 py-5">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Alapadatok</h2>
        <Mezo label="Név" {...szoveges('nev')} />
        <Mezo label="Slug (az oldal címe)" {...szoveges('slug')} hiba={slugFoglalt ? 'Ez a slug már foglalt.' : probalt ? hibak.slug : undefined} suly="Pl. duna-house-xiii; a /slug alatt érhető el." autoCapitalize="none" />
        <label className="flex min-h-11 items-center gap-3">
          <input type="checkbox" className="size-5 accent-kiemelt" checked={adat.aktiv} onChange={(e) => mutat('aktiv')(e.target.checked)} />
          Aktív (az oldal és a varázsló elérhető)
        </label>
        <Valaszto label="Hálózat (logóhoz)" value={adat.halozatKulcs ?? ''} onChange={(e) => mutat('halozatKulcs')(e.target.value === 'duna_house' || e.target.value === 'otthon_centrum' ? e.target.value : null)}>
          <option value="">Nincs</option>
          <option value="duna_house">Duna House</option>
          <option value="otthon_centrum">Otthon Centrum</option>
        </Valaszto>
        <Mezo label="Hálózat (belső megjegyzés)" {...szoveges('halozat')} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Mezo label="Régió" {...szoveges('regio')} />
          <Mezo label="Város" {...szoveges('varos')} />
        </div>
        <Mezo label="Logó URL" {...szoveges('logoUrl')} inputMode="url" />
      </section>

      <section className="grid gap-4 px-5 py-5">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Kapcsolat</h2>
        <Mezo label="E-mail" {...szoveges('kapcsolatEmail')} inputMode="email" />
        <Mezo label="Telefon" {...szoveges('kapcsolatTelefon')} inputMode="tel" />
      </section>

      <section className="grid gap-4 px-5 py-5">
        <h2 className="text-lg font-medium tracking-[-0.015em]">„Hívjon most”</h2>
        <Mezo label="Az iroda hívható száma" {...szoveges('hivasTelefon')} inputMode="tel" suly="+36… formátumban; üresen a gomb nem jelenik meg." />
        <MunkaidoSzerkeszto ertek={adat.munkaido} onValtoztat={mutat('munkaido')} />
      </section>

      {!uj && (
        <KiosztasBlokk
          officeId={id!} mod={adat.kiosztasMod} vezetoId={adat.kiosztasVezetoId}
          onMod={mutat('kiosztasMod')} onVezeto={mutat('kiosztasVezetoId')} hiba={probalt ? hibak.kiosztasVezetoId : undefined}
        />
      )}

      <section className="grid gap-4 px-5 py-5">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Válaszidő (SLA)</h2>
        <Mezo
          label="Visszahívási határidő (perc)" type="number" inputMode="numeric" min={15} max={1440}
          value={Number.isFinite(adat.slaPerc) ? String(adat.slaPerc) : ''}
          onChange={(e) => mutat('slaPerc')(e.target.value === '' ? Number.NaN : Number(e.target.value))}
          hiba={probalt ? hibak.slaPerc : undefined}
        />
      </section>

      {!uj && <KorzetBlokk officeId={id!} varos={betoltes.data?.varos ?? null} />}

      <section className="grid gap-4 px-5 py-5">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Minőségellenőrzés</h2>
        <Mezo
          label="A minőségellenőrzésről szóló ÁSZF 1.1 elfogadva" type="date" value={adat.minosegElfogadva}
          onChange={(e) => mutat('minosegElfogadva')(e.target.value)} hiba={probalt ? hibak.minosegElfogadva : undefined}
          suly="A partner-ÁSZF 1.1 (4.8. pont) kifejezett elfogadásának napja. Üresen az iroda leadjei nem kapnak kérdőívet; csak az elfogadás napján vagy utána megerősített lead számít."
        />
      </section>

      <section className="grid gap-4 px-5 py-5">
        <h2 className="text-lg font-medium tracking-[-0.015em]">Hirdetési link</h2>
        {fbLink ? (
          <>
            <div className="flex flex-wrap items-end gap-3">
              <Mezo className="min-w-0 flex-1" label="Facebook" value={fbLink} readOnly onFocus={(e) => e.currentTarget.select()} />
              <Gomb valtozat="masodlagos" onClick={() => void masol()}>Másolás</Gomb>
            </div>
            {masolas && <p role="status" className="text-sm text-szoveg-halk">{masolas === 'kesz' ? 'Kimásolva.' : 'Nem sikerült a másolás, jelöld ki a linket, és másold kézzel.'}</p>}
            <p className="text-sm text-szoveg-halk">Ezt a linket használja a kampányban. Az iroda kampánya a Meta-fiókban „ÉB | {adat.slug.trim()} | &lt;cél&gt;” nevű legyen.</p>
          </>
        ) : (
          <p className="text-sm text-szoveg-halk">A hirdetési link a slug megadása után jelenik meg.</p>
        )}
      </section>
      {regisztracio.data && (
        <section className="grid gap-3 px-5 py-5" aria-labelledby="cegadatok-cim">
          <h2 id="cegadatok-cim" className="text-lg font-medium tracking-[-0.015em]">Cégadatok a regisztrációból</h2>
          <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {[
              ['Cégnév', regisztracio.data.cegnev],
              ['Székhely', regisztracio.data.szekhely],
              ['Adószám', regisztracio.data.adoszam],
              [regisztracio.data.ceg_forma === 'tarsasag' ? 'Cégjegyzékszám' : 'Nyilvántartási szám', regisztracio.data.cegjegyzekszam ?? regisztracio.data.ev_nyilvantartasi_szam],
              ['Számlázási e-mail', regisztracio.data.szamlazasi_email],
              ['Hálózat a regisztrációkor', halozatNev(regisztracio.data)],
              ['Elfogadott ÁSZF', `${regisztracio.data.aszf_verzio} verzió, ${formatDatum(regisztracio.data.elfogadva_at)}`],
            ].map(([cimke, ertek]) => (
              <div key={cimke}><dt className="text-szoveg-halk">{cimke}</dt><dd className="wrap-anywhere font-medium">{ertek || '–'}</dd></div>
            ))}
          </dl>
          <Link to={`/super/regisztraciok/${regisztracio.data.id}`} className="text-sm text-kiemelt underline">A regisztráció megnyitása</Link>
        </section>
      )}
      </Fokonyv>

      <HibaUzenet hiba={mentes.error && (mentes.error as { code?: string }).code !== '23505' ? mentes.error : null} />
      {mentve && <p role="status" className="rounded-xl border border-siker px-3.5 py-2.5 text-sm text-siker">Mentve.</p>}
      <div className="flex flex-wrap gap-3">
        <Gomb type="submit" toltes={mentes.isPending}>Mentés</Gomb>
        {!uj && (
          <>
            <Gomb valtozat="masodlagos" onClick={() => { beallit(id!); void navigate('/kollegak'); }}>Tagok kezelése</Gomb>
            <a className="inline-flex min-h-10 items-center rounded-xl px-4 text-sm text-kiemelt underline" href={`${ELO_ALAP}/${adat.slug}`} target="_blank" rel="noreferrer">Élő oldal</a>
          </>
        )}
      </div>

      <Parbeszed nyitva={megerosit} cim="Biztosan inaktiválod?" onBezar={() => setMegerosit(false)}>
        <p>Az iroda oldala és varázslója azonnal elérhetetlenné válik.</p>
        <div className="flex flex-wrap justify-end gap-3">
          <Gomb valtozat="masodlagos" onClick={() => setMegerosit(false)}>Mégsem</Gomb>
          <Gomb valtozat="veszely" onClick={() => { setMegerosit(false); mentes.mutate(); }}>Inaktiválás és mentés</Gomb>
        </div>
      </Parbeszed>
    </form>
  );
}
